import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';

import {
  catchError,
  map,
  filter,
  Observable,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { CountryCode, parsePhoneNumber } from 'libphonenumber-js';

import { CheckEmailAndGoogleIdResponse } from '@shared/signup/models';
import { CheckCurrencyConversionRequest } from '@shared/stripe/models';
import { ApiClientService, StripeService } from '@tablesready/api-client';
import { DYNAMICFIELDS } from '../../constants';
import { SMS_REPLY_PATTERN } from '../constants';

const LINKS_WATCHER_MESSAGES: Record<string, string> = {
  booking: '{link} needs online reservations. Turn them on in Settings > Reservation Options, or remove {link}.',
  waitlist: '{link} needs Public waitlist view mode. Turn it on in Settings > Public Pages, or remove {link}.',
};

@Injectable({ providedIn: 'root' })
export class ValidationService {
  SHORTEST_PHONE_DIGITS = 4;

  constructor(
    private readonly http: ApiClientService,
    private readonly stripeApiService: StripeService,
  ) { }

  static getValidatorErrorMessage(validatorName: string, errorValue?: any) {
    const config = {
      required: 'This field is required.',
      pattern: 'This field is not valid.',
      email: 'Please enter email in the correct format.',
      notUuid: 'Not a valid UUID',
      phonePatternCountry:
        'Invalid. Do not use a country code (+1, +44, etc.) for numbers in your own country, but make sure to use them for international numbers.',
      min: 'This is smaller than the min allowed size.',
      max: 'This is bigger than the max allowed size.',
      minlength: 'This field is too short.',
      maxlength: 'This field is too long.',
      recordAvailability: 'This is already in use.',
      equal: 'Passwords should match',
      textWatcher: 'Message is too long',
      noWhitespace: 'This field includes only a space or is empty.',
      hours: 'Close time must be after open time.',
      nextDay: 'Close time must be before open time.',
      overlap: 'Open hours must not overlap with other schedules.',
      notUrl: 'Not a valid URL',
      validTime: 'Not a valid time.',
      invalidGoogleAddress: 'Please select the most correct address',
      lettersNumbersHyphenQuotesSpacesCommas: 'Must only contain numbers, letters and hyphens and quotes and commas',
      lettersNumbersHyphenQuotes: 'Must only contain numbers, letters and hyphens and quotes',
      linksWatcher: LINKS_WATCHER_MESSAGES[errorValue?.value] ?? LINKS_WATCHER_MESSAGES.waitlist,
      dockmagicPattern:'Fill in at least one of the fields: "Carrier", "Truck" or "DeliveryId".',
      forbiddenWords: `Keywords "${errorValue?.words?.join(', ')}" are reserved and cannot be used.`,
      forbiddenNames: `This app is for businesses. If you are trying to book an appointment or sign up for a waitlist, please contact the business for the correct URL.`,
      textLengthWatcher: 'Message is too long',
      incorrectEmail: errorValue?.value || 'Invalid email address.',
      passwordConfirmation: 'Passwords should match.',
      passwordValidator: 'Must have a character, number and special symbol',
      invalidLinks: 'Please check if you type the dynamic value correctly',
      url: 'Invalid url',
      bitly: 'bit.ly links are blocked as spam by most carriers. Please use the full URL.',
      uniqueId: 'Id must be unique.',
      price: 'Value is not a valid price',
      priceMin: 'Price can’t be less than $1',
      currencyConversion: 'Converted amount can’t be less than $1'
    };

    return config[validatorName] || null;
  }

  validateCurrencyConversion = (data: CheckCurrencyConversionRequest): Observable<null | { currencyConversion: boolean }> => {
    return this.stripeApiService.checkCurrencyConversion(data)
      .pipe(
        map(result => result < 1 ? { currencyConversion: true } : null),
      );
  }

  validateEmail = (email: string): Observable<any> => {
    if(email) {
      return this.http.post('/signup/checkEmailValid', {email})
      .pipe(
        map(() => {
          return null;
        }),
        catchError(err => {
          return of({ incorrectEmail: { value: err.error.message } });
        })
      )
    } else {
      return of(null);
    }
  }

  validateGoogleAddress(control: AbstractControl): null | { invalidGoogleAddress: boolean } {
    if (control.value === null) {
      return { invalidGoogleAddress: true };
    }

    return null;
  }

  emailUsedValidator = (
    email: string,
    restaurantId: string = '',
  ): Observable<null | { recordAvailability: boolean }> => {
    if (email) {
      return this.http
        .post('/signup/checkEmailUsed', {
          modelValue: email,
          restaurantId,
        }, { responseType: 'text' })
        .pipe(
          map(() => null),
          catchError(() => of({ recordAvailability: true }))
        );
    }

    return of(null);
  };

  emailAngGoogleIdUsed(token: string): Observable<CheckEmailAndGoogleIdResponse> {
    return this.http.post<CheckEmailAndGoogleIdResponse>('/signup/checkEmailAndGoogleIdUsed', { token });
  }

  usernameUsedValidator = (
    userName: string,
    restaurantId: string = '',
  ): Observable<null | { recordAvailability: boolean }> => {
    return this.http
      .post(
        '/signup/checkUsernameUsed',
        { modelValue: userName, restaurantId },
        // ts-expect-error not a json
        { responseType: 'text' }
      )
      .pipe(
        map(() => {
          return null;
        }),
        catchError(() => {
          return of({ recordAvailability: true });
        })
      );
  };

  optionalZeroBounceValidation(control: AbstractControl, beforeStart: () => void): Observable<string> {
    return control.valueChanges
      .pipe(
        tap(() => {
          beforeStart();
        }),
        filter(() => control.status !== 'INVALID'),
        switchMap(email => this.validateEmail(String(email))),
        map((emailErrors) => {
          if (emailErrors === null) {
            return null;
          }

          return 'ZeroBounce validation failed';
        }),
      );
  }

  publicVenueNameUsedValidator(
    publicVenueName: string,
    restaurantId: string = ''
  ): Observable<null | { recordAvailability: boolean }> {
    if (!publicVenueName) {
      return of(null);
    }

    return this.http.post(
      '/signup/checkPublicNameUsed',
      { modelValue: publicVenueName, restaurantId },
      { responseType: 'text' },
    )
      .pipe(
        map(() => {
          return null;
        }),
        catchError(() => {
          return of({ recordAvailability: true })
        }),
      )
  }

  isUuidValid(uuid: string): boolean {
    const expression = /^[a-z,0-9,-]{36,36}$/;
    const regexp = new RegExp(expression);
    const isUuid = String(uuid).match(regexp);

    return isUuid !== null;
  }

  phonePatternCountryValidator(
    getCountryAbbr: () => string
  ): (control: AbstractControl) => { [key: string]: any } | null;
  phonePatternCountryValidator(
    countryAbbr: string
  ): (control: AbstractControl) => { [key: string]: any } | null;
  phonePatternCountryValidator(
    countryAbbr: any
  ): (control: AbstractControl) => { [key: string]: any } | null {
    return (control: AbstractControl): { [key: string]: any } | null => {
      let isValid = false;
      const phone: string = control.value;
      const countryAbbrValue = typeof countryAbbr === 'function' ? countryAbbr() : countryAbbr;
      const countryCode: CountryCode =
        countryAbbrValue === 'UK' ? 'GB' : (countryAbbrValue as CountryCode);

      if (!phone) {
        return null;
      }

      if (phone.length < this.SHORTEST_PHONE_DIGITS) {
        isValid = false;
      } else if (phone.charAt(0) === '+') {
        isValid = this.isValid(phone)
      } else {
       isValid = this.isValid(phone, countryCode);
      }

      return isValid
        ? null
        : {
            phonePatternCountry: { value: phone },
          };
    };
  }

  lettersNumbersHyphenQuotesSpacesCommas(): (control: AbstractControl) => Record<string, unknown> | null {
    return (control) => {
      if (control.value === '') {
        return null;
      }

      const expression = /^([a-zA-Z0-9-_"',]+\s)*[a-zA-Z0-9-_"',]+$/;
      const regexp = new RegExp(expression);
      const doesMatch = String(control.value).match(regexp);

      return doesMatch ? null : { lettersNumbersHyphenQuotesSpacesCommas: true };
    };
  }

  lettersNumbersHyphenQuotesSpaces(): (control: AbstractControl) => Record<string, unknown> | null {
    return (control) => {
      if (control.value === '') {
        return null;
      }

      const expression = /^([a-zA-Z0-9-_"']+\s)*[a-zA-Z0-9-_"']+$/
      const regexp = new RegExp(expression);
      const doesMatch = String(control.value).match(regexp);

      return doesMatch ? null : { lettersNumbersHyphenQuotes: true };
    };
  }

  isValid(phone: string, countryCode?: CountryCode): boolean {
    try {
      return parsePhoneNumber(phone, countryCode).isValid();
    } catch (error) {
      return false;
    }
  }

  textWatcherValidator(
    pageView: boolean,
    maxAnsiChars: number,
    maxNonAnsiChars: number,
    pageText: string,
  ): (control: AbstractControl) => Record<string, any> | null {
    return (control: AbstractControl): Record<string, any> | null => {
      const text = control.value;
      if (!text) {
        return null;
      }

      const links = text.match(/[^{]+(?=\})/g);
      const isCharts = this.notificationLength(text) <= this.chartsLength(text, maxAnsiChars, maxNonAnsiChars);

      if (links?.length) {
        if (links.indexOf('link') > -1 && !pageView) {
          return { linksWatcher: { value: pageText } };
        }

        const invalidLinks = links.filter(link => !this.getValidLinks().includes(link));
        if (invalidLinks.length > 0) {
          return { invalidLinks: true };
        }
      }

      return isCharts ? null : { textWatcher: { value: text } };
    };
  }

  getValidLinks() {
    return DYNAMICFIELDS.map(field => field.name.replace(/[{}]/g, ''));
  }

  notificationLength(str: string): number {
    const _str = typeof str === 'string' ? str : '';
    let len = _str.length;
    DYNAMICFIELDS.forEach((field) => {
      if (_str.indexOf(`{${field.name}}`) !== -1) {
        len += field.defaultLength - `{${field.name}}`.length;
      }
    });
    return len;
  }

  chartsLength(str: string, maxAnsiChars: number, maxNonAnsiChars: number): number {
    return new RegExp(SMS_REPLY_PATTERN).test(str) ? maxAnsiChars : maxNonAnsiChars;
  }

  textLengthValidator(
    maxAnsiChars: number,
    maxNonAnsiChars: number,
  ): (control: AbstractControl) => { [key: string]: any } | null {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const text: string = control.value;
      if (!text || text === '') {
        return null;
      }
      const isExceededLength = text.length >= this.chartsLength(text, maxAnsiChars, maxNonAnsiChars);

      return  isExceededLength ? {textLengthWatcher: { value: text }} : null;
    };
  }

  conditionallyRequiredValidator(condition): ValidatorFn {
    return ( (formControl: AbstractControl) => {
      if (condition()) {
        return Validators.required(formControl);
      }
      return null;
    })
  }

  passwordValidator(): ValidatorFn {
    return(control: AbstractControl): { [key: string]: any } => {
      const hasNumber = new RegExp(/\d/);
      const hasCharacter = new RegExp(/[A-zÀ-ž][A-zÀ-ž\\/]*[A-zÀ-ž-\'" ]*[A-zÀ-ž\'"]/);
      const hasSpecialSymbol = new RegExp(/[_@.#$=!%^)(\]:\*;\?\/\,}{\'\|<>\\[&\+-]/);

      if(!control.value){
        return null;
      }
      const valid = hasNumber.test(control.value) && hasCharacter.test(control.value) && hasSpecialSymbol.test(control.value);
      return valid ? null : {passwordValidator: true};
    }
  }

  serviceUrlUsedValidator(url: string, _id?: string): Observable<null | { recordAvailability: boolean }> {
    return this.http.post('/v.1/services/checkUrl', { url, _id }, { responseType: 'text' })
      .pipe(
        map(() => null),
        catchError(() => of({recordAvailability: true})),
      );
  }

  accountCodeUsedValidator = (accountCode: string, restaurantId?: string): Observable<null | {recordAvailability: boolean;}> => {
    if(accountCode) {
      return this.http
        .post('/v.1/admin/account/checkAccountCode', {modelValue: accountCode, restaurantId},)
        .pipe(
          map(() => null),
          catchError(() => of({recordAvailability: true})),
        );
    }

    return of(null);
  };

  validateForm(form: FormGroup<unknown>): void {
    Object.keys(form.controls).forEach((field) => {
      const control = form.get(field);
      this.validateField(control);
    });
  }

  validateField(control: AbstractControl): void {
    control.markAsTouched();
  }
}
